<template>
    <div class="lg:flex lg:h-full lg:flex-col">
        <calendar-navigation
            :meta="meta"
            view="month"
            :show-view-switch="showViewSwitch"
            @set-view="setView"
            @set-date="setDate"
        ></calendar-navigation>

        <layout-box>
            <div class="lg:flex lg:flex-auto lg:flex-col">
                <div
                    class="grid grid-cols-7 gap-px text-xs font-semibold leading-6 text-center text-gray-700 bg-gray-200 border border-b-0 border-gray-300 dark:border-zinc-700 dark:text-zinc-200 dark:bg-zinc-700 lg:flex-none"
                >
                    <div class="py-2 bg-white dark:bg-zinc-800">P<span class="sr-only sm:not-sr-only">o</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">Ú<span class="sr-only sm:not-sr-only">t</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">S<span class="sr-only sm:not-sr-only">t</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">Č<span class="sr-only sm:not-sr-only">t</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">P<span class="sr-only sm:not-sr-only">á</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">S<span class="sr-only sm:not-sr-only">o</span></div>
                    <div class="py-2 bg-white dark:bg-zinc-800">N<span class="sr-only sm:not-sr-only">e</span></div>
                </div>
                <div
                    class="flex text-xs leading-6 text-gray-700 bg-gray-200 border border-gray-300 dark:text-zinc-200 dark:bg-zinc-700 lg:flex-auto dark:border-zinc-700"
                >
                    <div class="hidden w-full lg:grid lg:grid-cols-7 lg:gap-px">
                        <div
                            v-for="day in days"
                            :key="day.date"
                            :class="[
                                day.isCurrentMonth
                                    ? 'bg-white dark:text-zinc-50 dark:bg-zinc-800'
                                    : 'bg-gray-50 text-gray-500 dark:bg-zinc-700',
                                'relative py-2 px-3 min-h-[120px] group/day',
                            ]"
                        >
                            <time
                                :datetime="day.date"
                                :class="
                                    day.isToday
                                        ? 'flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 font-semibold text-white'
                                        : undefined
                                "
                                >{{ day.date.split('-').pop().replace(/^0/, '') }}</time
                            >
                            <ol v-if="day.events.length > 0" class="mt-2">
                                <li v-for="event in day.events.slice(0, 2)" :key="event.id">
                                    <inertia-link
                                        :href="event.url"
                                        class="flex px-2 border rounded group"
                                        :class="[
                                            `bg-${event.color}-100 hover:bg-${event.color}-200 border-${event.color}-400 dark:bg-${event.color}-700/30 dark:border-${event.color}-700 dark:hover:bg-${event.color}-900`,
                                        ]"
                                    >
                                        <p
                                            class="flex-auto font-medium text-gray-900 truncate dark:text-zinc-50"
                                            :class="[`group-hover:text-${event.color}-800`]"
                                        >
                                            {{ event.name }}
                                        </p>
                                        <time
                                            :datetime="event.datetime"
                                            class="flex-none hidden ml-3 xl:block"
                                            :class="[`text-${event.color}-500 group-hover:text-${event.color}-800`]"
                                            >{{ event.time }}</time
                                        >
                                    </inertia-link>
                                </li>
                                <li
                                    v-for="event in day.events.slice(2, day.events.length)"
                                    :key="event.id"
                                    class="hidden group-hover/day:block"
                                >
                                    <inertia-link
                                        :href="event.url"
                                        class="flex px-2 border rounded group"
                                        :class="[
                                            `bg-${event.color}-100 hover:bg-${event.color}-200 border-${event.color}-400 dark:bg-${event.color}-700/30 dark:border-${event.color}-700 dark:hover:bg-${event.color}-900`,
                                        ]"
                                    >
                                        <p
                                            class="flex-auto font-medium text-gray-900 truncate dark:text-zinc-50"
                                            :class="[`group-hover:text-${event.color}-800`]"
                                        >
                                            {{ event.name }}
                                        </p>
                                        <time
                                            :datetime="event.datetime"
                                            class="flex-none hidden ml-3 xl:block"
                                            :class="[`text-${event.color}-500 group-hover:text-${event.color}-800`]"
                                            >{{ event.time }}</time
                                        >
                                    </inertia-link>
                                </li>
                                <li v-if="day.events.length > 2" class="text-gray-500 group-hover/day:hidden">
                                    + {{ day.events.length - 2 }} {{ lang('general.more') }}
                                </li>
                            </ol>
                        </div>
                    </div>
                    <div class="grid w-full grid-cols-7 gap-px isolate lg:hidden">
                        <button
                            v-for="day in days"
                            :key="day.date"
                            type="button"
                            :class="[
                                day.isCurrentMonth ? 'bg-white dark:bg-zinc-800' : 'bg-gray-50 dark:bg-zinc-700',
                                (day.isSelected || day.isToday) && 'font-semibold',
                                day.isSelected && 'text-white',
                                !day.isSelected && day.isToday && 'text-brand-800',
                                !day.isSelected &&
                                    day.isCurrentMonth &&
                                    !day.isToday &&
                                    'text-gray-900 dark:text-zinc-50',
                                !day.isSelected &&
                                    !day.isCurrentMonth &&
                                    !day.isToday &&
                                    'text-gray-500 dark:text-zinc-300',
                                'flex h-14 flex-col py-2 px-3 hover:bg-gray-100 focus:z-10',
                            ]"
                        >
                            <time
                                :datetime="day.date"
                                :class="[
                                    day.isSelected && 'flex h-6 w-6 items-center justify-center rounded-full',
                                    day.isSelected && day.isToday && 'bg-brand-800',
                                    day.isSelected && !day.isToday && 'bg-gray-900',
                                    'ml-auto',
                                ]"
                                >{{ day.date.split('-').pop().replace(/^0/, '') }}</time
                            >
                            <span class="sr-only">{{ day.events.length }} events</span>
                            <span v-if="day.events.length > 0" class="-mx-0.5 mt-auto flex flex-wrap-reverse">
                                <inertia-link
                                    v-for="event in day.events"
                                    :key="event.id"
                                    :href="event.url"
                                    class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full"
                                    :class="`bg-${event.color}-400 dark:bg-${event.color}-700`"
                                />
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </layout-box>

        <div v-if="selectedDay?.events.length > 0" class="px-4 py-10 sm:px-6 lg:hidden">
            <ol
                class="overflow-hidden text-sm bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-zinc-800 ring-1 ring-black/5"
            >
                <li
                    v-for="event in selectedDay.events"
                    :key="event.id"
                    class="flex p-4 pr-6 group focus-within:bg-gray-50 hover:bg-gray-50"
                >
                    <div class="flex-auto">
                        <p class="font-semibold text-gray-900 dark:text-zinc-50">{{ event.name }}</p>
                        <time :datetime="event.datetime" class="flex items-center mt-2 text-gray-700">
                            <icon
                                icon="hero/outline/clock"
                                class="w-5 h-5 mr-2 text-gray-400"
                                aria-hidden="true"
                            ></icon>
                            {{ event.time }}
                        </time>
                    </div>
                    <inertia-link
                        :href="event.href"
                        class="self-center flex-none px-3 py-2 ml-6 font-semibold text-gray-700 border border-gray-300 rounded-md shadow-sm opacity-0 dark:text-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 hover:bg-gray-50 focus:opacity-100 group-hover:opacity-100"
                        >Edit<span class="sr-only">, {{ event.name }}</span></inertia-link
                    >
                </li>
            </ol>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import CalendarNavigation from '@src/Reservations/Pages/Calendar/Partials/Navigation.vue';

const props = defineProps({
    date: {
        type: Object,
        default: () => ({
            day: null,
            month: null,
            year: null,
        }),
    },
    showViewSwitch: {
        type: Boolean,
        default: true,
    },
    action: {
        type: String,
        default: 'reservations.calendar.api.month',
    },
    actionParams: {
        type: Object,
        default: () => ({}),
    },
});

const showViewSwitch = ref(props.showViewSwitch);

const emit = defineEmits(['setView', 'setDate']);

const setView = (view: string) => emit('setView', view);

const days = ref([]);
const meta = ref([]);
const selectedDay = days.value.find((day) => day.isSelected);

const getData = ({ day, month, year } = { day: null, month: null, year: null }) => {
    axios
        .get(route(props.action, props.actionParams), {
            params: {
                day,
                month,
                year,
            },
        })
        .then(({ data }) => {
            // The global interceptor resolves 4xx/5xx with {success:false} — no calendar data
            if (!data?.days || !data?.meta) {
                return;
            }

            days.value = data.days;
            meta.value = data.meta;
        });
};

onMounted(() => {
    getData(props.date);
});

const setDate = ({ day, month, year }) => {
    getData({ day, month, year });

    emit('setDate', { day, month, year });
};
</script>
